import debounce from 'lodash/debounce'
import * as React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import type { State } from 'types/redux'
import {
  campaignRecipientAdd,
  campaignRecipientChange,
  campaignRecipientModeSelect,
  campaignRecipientRemove,
} from '@disco/state-management/actions/campaign-recipient-actions'
import { loadEmailGroups } from '@disco/state-management/actions/EmailGroupActions'
import type { MatchMode } from '@disco/state-management/reducers/email-campaigns/recipient-rules'
import { getCampaignRules } from '@disco/state-management/selectors/campaigns'
import TagSelector from 'components/common/tag-selector'
import ChoiceList from 'components/ui/choice-list'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import Label from 'components/ui/label'
import TextField from 'components/ui/text-field'
import CloseBtn from './close-btn'
import styles from './index.module.css'

function mapStateToProps(state: State) {
  return {
    emailGroups: state.emailGroups,
    recipientRules: getCampaignRules(state),
  }
}

const mapActionsToProps = {
  loadEmailGroups,
  campaignRecipientModeSelect,
  campaignRecipientAdd,
  campaignRecipientRemove,
  campaignRecipientChange,
}
const defaultRuleOps: RuleOp[] = [
  'equals',
  'notEquals',
  'contains',
  'notContains',
]
const ruleOpMapping: Record<RuleOp, string> = {
  equals: 'is',
  notEquals: 'is not',
  contains: 'contains',
  notContains: 'does not contain',
}
type RuleOp = 'equals' | 'notEquals' | 'contains' | 'notContains'
type RuleType = {
  label: string
  ruleType: string
  ruleOps: RuleOp[]
  deprecated?: boolean
  icon?: string
}
const rules: RuleType[] = [
  {
    label: 'Mailing list',
    ruleType: 'mailingList',
    ruleOps: defaultRuleOps,
    icon: 'playlist-add',
  },
  {
    label: 'Name',
    ruleType: 'name',
    ruleOps: defaultRuleOps,
    icon: 'contact-mail',
  },
  {
    label: 'Company',
    ruleType: 'company',
    ruleOps: defaultRuleOps,
    icon: 'business-center',
  },
  {
    label: 'Email',
    ruleType: 'email',
    ruleOps: defaultRuleOps,
    icon: 'send',
  },
  {
    label: 'Location',
    ruleType: 'territory',
    ruleOps: defaultRuleOps,
    icon: 'pin-circle',
  },
  {
    label: 'Contact tag',
    ruleType: 'tags',
    ruleOps: ['equals', 'notEquals'],
    icon: 'playlist-add',
  },
  {
    // this rule type is left for backwards compatibility
    label: 'Contact type',
    ruleType: 'type',
    ruleOps: defaultRuleOps,
    deprecated: true,
  },
]
const availableRules = rules.filter(r => !r.deprecated)
const modes = [
  {
    label: 'All these rules',
    value: 'all',
  },
  {
    label: 'Any of these rules',
    value: 'any',
  },
]

class CampaignRecipientRules extends React.Component<{
  recipientRules: State['emailCampaigns']['recipientRules']
  emailGroups: State['emailGroups']
  campaignRecipientModeSelect: typeof campaignRecipientModeSelect
  campaignRecipientAdd: typeof campaignRecipientAdd
  campaignRecipientRemove: typeof campaignRecipientRemove
  campaignRecipientChange: typeof campaignRecipientChange
  loadEmailGroups: typeof loadEmailGroups
}> {
  componentDidMount() {
    this.props.loadEmailGroups()
  }

  _handleChangeMode = (key: MatchMode, value: boolean) => {
    this.props.campaignRecipientModeSelect(key)
  }
  _handleChangeRuleMode = (ruleId, ruleOp) => {
    this.props.campaignRecipientChange(ruleId, {
      ruleOp,
    })
  }
  _handleChangeRuleValue = debounce((ruleId, ruleValue) => {
    this.props.campaignRecipientChange(ruleId, {
      ruleValue,
    })
  }, 300)
  _onChange = (evt: React.SyntheticEvent<HTMLInputElement>) => {
    const ruleId = evt.currentTarget.name
    const ruleValue = evt.currentTarget.value

    this._handleChangeRuleValue(ruleId, ruleValue)
  }
  _onTagsSelect = (ruleId, tag) => {
    this._handleChangeRuleValue(ruleId, tag ? tag.value : '')
  }
  _handleRemoveRule = ruleId => {
    this.props.campaignRecipientRemove(ruleId)
  }
  _handleAddRule = ruleType => {
    this.props.campaignRecipientAdd(ruleType)
  }

  _renderModeSelect() {
    const { recipientRules } = this.props
    return (
      <div className={styles.campaignRecipientRulesRuleCont}>
        <ChoiceList
          multiple={false}
          title="Users who match"
          items={modes}
          selected={recipientRules.matchMode}
          onChange={this._handleChangeMode}
        />
      </div>
    )
  }

  _renderMailingListRule(rule) {
    const availableRule = rules.find(r => r.ruleType === rule.ruleType)
    const label = availableRule ? availableRule.label : 'Unknown'
    return (
      <div key={rule.id} className={styles.campaignRecipientRulesRuleCont}>
        <div className={styles.campaignRecipientRulesCloseBtn}>
          <CloseBtn onClick={() => this._handleRemoveRule(rule.id)} />
        </div>
        <Label caption={label} />
        <CustomDropdown
          options={this.props.emailGroups.emailGroups.map(item => ({
            label: item.name,
            value: item.id.toString(),
          }))}
          multi={false}
          clearable={false}
          value={rule.ruleValue}
          onChange={ruleValue => {
            this.props.campaignRecipientChange(rule.id, {
              ruleValue: ruleValue.value,
            })
          }}
        />
        <div className={styles.campaignRecipientRulesListTip}>
          Need to set up your mailing lists?&nbsp;
          <Link to="/lists">Do that here.</Link>
        </div>
      </div>
    )
  }

  _renderRule(rule) {
    if (rule.ruleType === 'mailingList')
      return this._renderMailingListRule(rule)
    const availableRule = rules.find(r => r.ruleType === rule.ruleType)
    const label = availableRule ? availableRule.label : 'Unknown'
    const options = availableRule && (
      <ChoiceList
        multiple={false}
        title={label}
        items={availableRule.ruleOps.map(op => ({
          label: ruleOpMapping[op],
          value: op,
        }))}
        selected={rule.ruleOp}
        onChange={value => this._handleChangeRuleMode(rule.id, value)}
      />
    )
    const input =
      rule.ruleType !== 'tags' ? (
        <TextField
          name={rule.id}
          defaultValue={rule.ruleValue}
          onChange={this._onChange}
        />
      ) : (
        <TagSelector
          onChange={tags => this._onTagsSelect(rule.id, tags)}
          selectedTags={rule.ruleValue ? rule.ruleValue : ''}
          placeholder="Select tag"
          tagsType="contact"
          multi={false}
        />
      )
    return (
      <div key={rule.id} className={styles.campaignRecipientRulesRuleCont}>
        <div className={styles.campaignRecipientRulesCloseBtn}>
          <CloseBtn onClick={() => this._handleRemoveRule(rule.id)} />
        </div>
        {options}
        {input}
      </div>
    )
  }

  _renderAdding() {
    return (
      <div className={styles.campaignRecipientRulesRuleCont}>
        <EllipsisMenu
          className={styles.campaignRecipientRulesEllipsisMenu}
          caption="Add a rule"
          compact={true}
          icon="pen"
          options={availableRules.map(rule => ({
            label: rule.label,
            value: rule.ruleType,
            icon: rule.icon,
          }))}
          onOptionClick={this._handleAddRule}
        />
      </div>
    )
  }

  render() {
    return (
      <div className={styles.campaignRecipientRules}>
        <div className={styles.campaignRecipientRulesCont}>
          {this._renderModeSelect()}
          {this.props.recipientRules.rules.map(rule => this._renderRule(rule))}
          {this._renderAdding()}
        </div>
      </div>
    )
  }
}

export default connect(
  mapStateToProps,
  mapActionsToProps,
)(CampaignRecipientRules) as any
